import * as _ from 'underscore'

import { Enums } from '../../../../models/common'
import { OpView, OpViewColumn } from '../../../../models/core'
import { GetViewColumnOptions } from '../../../../models/core/op-view'
import { OpMarker } from '../../../../models/custom'
import { OpCoord } from '../../../../models/custom/op-map-area'
import { IOpObject, IReferenceType } from '../../../../models/util/interfaces'
import { ObjectCustomString } from '../../../../models/util'

import { TypeHelper, ConvertHelper } from '../../../../helpers'

//#region Public Types

export const MAP_SERVICE_NAME = 'MapService'
export const MAP_SERVICE_PROVIDER_NAME = 'MapServiceProvider'

//#endregion

type CreateOpMarkerOptions = {
    markerStringObject?: IOpObject | IReferenceType
    markerStringReferenceType?: Enums.ReferenceType
    view?: OpView

    onClick?: (marker: OpMarker) => void
}

type MarkerSourceObject = IOpObject | IReferenceType | { dynamicValues: { Key: string, Value: any }[] }


export class MapService {

    //#region Ctor

    constructor() { }

    //#endregion


    //#region Markers

    public createOpMarker(object: MarkerSourceObject, options: CreateOpMarkerOptions = {}) {
        if (object != null) {
            const marker: OpMarker = new OpMarker()

            if (TypeHelper.isIOpObject(object)) {
                marker.referenceValue = object.idObject
            } else if (TypeHelper.isIReferenceType(object)) {
                marker.referenceValue = object.getReferenceKey()
            }

            const coords: [number, number] = this.getCoords(object, options)

            if (coords == null) {
                return null
            }

            marker.coords = new OpCoord()
            marker.coords.latitude = coords[0]
            marker.coords.longitude = coords[1]

            marker.tooltip = this.getMarkerString(object, options)
            marker.fillColor = this.getMarkerColor(object)

            if (options != null) {
                if (_.isFunction(options.onClick)) {
                    marker.onClick = () => {
                        options.onClick(marker)
                    }
                }
            }

            return marker
        }

        return null
    }

    public getCoords(object: MarkerSourceObject, options: CreateOpMarkerOptions): [number, number] {
        if (object != null && options != null) {
            if (options.view != null && TypeHelper.hasDynamicValues(object)) {
                return this.getCoordsFromDynamicValues(object.dynamicValues, options.view)
            }
        }

        return null
    }

    private getCoordsFromDynamicValues(dynamicValues: { Key: string, Value: any }[], view: OpView): [number, number] {
        if (dynamicValues != null && view != null) {
            const latitudeColumn: OpViewColumn = view.getViewColumn(new GetViewColumnOptions(Enums.DataType.LOCATION_LATITUDE))
            const longitudeColumn: OpViewColumn = view.getViewColumn(new GetViewColumnOptions(Enums.DataType.LOCATION_LONGITUDE))

            if (latitudeColumn != null && longitudeColumn != null) {
                const latitudeDynamicValue = _.find(dynamicValues, dynamicValue => dynamicValue.Key.toLowerCase() === latitudeColumn.name.toLowerCase())
                const longitudeDynamicValue = _.find(dynamicValues, dynamicValue => dynamicValue.Key.toLowerCase() === longitudeColumn.name.toLowerCase())

                if (
                    latitudeDynamicValue != null
                    && longitudeDynamicValue != null
                    && latitudeDynamicValue.Value != null
                    && longitudeDynamicValue.Value != null
                ) {
                    return [latitudeDynamicValue.Value, longitudeDynamicValue.Value]
                }
            }
        }

        return null
    }

    private getMarkerString(object: MarkerSourceObject, options: CreateOpMarkerOptions): string {
        let markerObject: MarkerSourceObject = null
        let customFormatString: string = null

        if (options != null) {
            if (options.markerStringObject != null) {
                markerObject = options.markerStringObject
            }

            if (options.markerStringReferenceType != null) {
                customFormatString = ObjectCustomString.getCutomMarkerStringFormat(null, options.markerStringReferenceType)
            }
        }

        if (markerObject == null) {
            markerObject = object
        }

        if (customFormatString == null && TypeHelper.isIReferenceType(markerObject)) {
            customFormatString = ObjectCustomString.getCutomMarkerStringFormat(markerObject)
        }

        if (customFormatString == null) {
            return markerObject.toString()
        }

        if (TypeHelper.isIOpObject(markerObject)) {
            return ObjectCustomString.getObjectCustomString(customFormatString, markerObject)
        }

        return null
    }

    private getMarkerColor(object: MarkerSourceObject): string {
        if (object != null) {
            if (TypeHelper.isIOpObject(object)) {
                const colorReferenceValue: Enums.KnownColorsEnum = object.dataList.getValue(Enums.DataType.HELPER_COLOR)

                if (colorReferenceValue != null) {
                    return ConvertHelper.knownColorEnumToHexColor(colorReferenceValue)
                }
            }
        }

        return null
    }

    //#endregion
}

export class MapServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [function () {
        return new MapService()
    }]

    //#endregion
} 